import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { transactionAPI } from '../services/api';
import {
  ArrowLeft, Plus, DollarSign, Calendar,
  Trash2, Download, User, Building
} from 'lucide-react';
import { formatDate, formatCurrency } from '../utils/formatters';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import { useToast } from '../contexts/ToastContext';
import Button from '../components/Button';
import * as XLSX from 'xlsx';
import NumberInput from '../components/NumberInput';

const toDateInputValue = (value) => {
  if (!value) return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
  } catch {
    return '';
  }
};

const ContractManagementFees = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { success, error: showError } = useToast();

  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingFeeId, setEditingFeeId] = useState(null);
  const [editForm, setEditForm] = useState({
    period_start: '',
    period_end: '',
    amount: 0,
    due_date: '',
    description: '',
    status: '미납'
  });
  const [newFee, setNewFee] = useState({
    period_start: '',
    period_end: '',
    amount: 0,
    due_date: '',
    description: '',
    status: '미납'
  });

  // 계약 정보 조회
  const { data: contract, isLoading: isLoadingContract } = useQuery({
    queryKey: ['contract', id],
    queryFn: () => transactionAPI.getById(id),
    select: (response) => response.data,
  });

  // 관리비 내역 조회
  const { data: feesData, isLoading: isLoadingFees } = useQuery({
    queryKey: ['contract-management-fees', id],
    queryFn: () => transactionAPI.getManagementFees(id),
    select: (response) => response.data?.data || [],
  });

  const managementFees = feesData || [];

  // 관리비 추가 뮤테이션
  const addManagementFeeMutation = useMutation({
    mutationFn: (feeData) => transactionAPI.addManagementFee(id, feeData),
    onSuccess: () => {
      queryClient.invalidateQueries(['contract-management-fees', id]);
      success('관리비가 추가되었습니다.');
      setShowAddForm(false);
      setNewFee({
        period_start: '',
        period_end: '',
        amount: 0,
        due_date: '',
        description: '',
        status: '미납'
      });
    },
    onError: (error) => {
      showError('관리비 추가에 실패했습니다.');
      console.error('관리비 추가 실패:', error);
    }
  });

  // 관리비 수정 (모달·상태 등)
  const updateManagementFeeMutation = useMutation({
    mutationFn: ({ feeId, ...data }) => transactionAPI.updateManagementFee(id, feeId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['contract-management-fees', id]);
      success('관리비가 수정되었습니다.');
      setShowEditModal(false);
      setEditingFeeId(null);
    },
    onError: (error) => {
      showError(error.response?.data?.message || '관리비 수정에 실패했습니다.');
      console.error('관리비 수정 실패:', error);
    }
  });

  // 관리비 삭제 뮤테이션
  const deleteFeeMutation = useMutation({
    mutationFn: (feeId) => transactionAPI.deleteManagementFee(id, feeId),
    onSuccess: () => {
      queryClient.invalidateQueries(['contract-management-fees', id]);
      success('관리비가 삭제되었습니다.');
    },
    onError: (error) => {
      showError('관리비 삭제에 실패했습니다.');
      console.error('관리비 삭제 실패:', error);
    }
  });

  if (!hasPermission(user?.role, 'MANAGEMENT_FEE_MANAGE')) {
    return (
      <div className="flex-center min-h-screen">
        <div className="text-center">
          <Calendar size={64} className="mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl font-semibold text-gray-600 mb-2">접근 권한이 없습니다</h2>
          <p className="text-gray-500">관리비 관리 권한이 필요합니다.</p>
        </div>
      </div>
    );
  }

  const handleAddFee = () => {
    if (!newFee.period_start || !newFee.period_end || !newFee.amount || !newFee.due_date) {
      showError('필수 항목을 모두 입력해주세요.');
      return;
    }
    addManagementFeeMutation.mutate(newFee);
  };

  const handleOpenEditModal = (fee) => {
    setEditingFeeId(fee.id);
    setEditForm({
      period_start: toDateInputValue(fee.period_start),
      period_end: toDateInputValue(fee.period_end),
      amount: parseFloat(fee.amount) || 0,
      due_date: toDateInputValue(fee.due_date),
      description: fee.description || '',
      status: fee.status || '미납'
    });
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingFeeId(null);
  };

  const handleSubmitEdit = () => {
    if (!editForm.period_start || !editForm.period_end || !editForm.due_date) {
      showError('관리기간·납부기한을 입력해주세요.');
      return;
    }
    if (editForm.amount === '' || editForm.amount == null || Number(editForm.amount) < 0) {
      showError('금액을 입력해주세요.');
      return;
    }
    if (!editingFeeId) return;
    updateManagementFeeMutation.mutate({
      feeId: editingFeeId,
      period_start: editForm.period_start,
      period_end: editForm.period_end,
      amount: editForm.amount,
      due_date: editForm.due_date,
      description: editForm.description,
      status: editForm.status
    });
  };

  const handleDeleteFee = (feeId) => {
    if (window.confirm('정말로 이 관리비를 삭제하시겠습니까?')) {
      deleteFeeMutation.mutate(feeId);
    }
  };

  // 엑셀 다운로드
  const handleExcelDownload = () => {
    if (!managementFees || managementFees.length === 0) {
      showError('다운로드할 데이터가 없습니다.');
      return;
    }

    try {
      const excelData = managementFees.map(fee => ({
        '관리기간': `${formatDate(fee.period_start)} ~ ${formatDate(fee.period_end)}`,
        '금액': formatCurrency(fee.amount),
        '납부기한': formatDate(fee.due_date),
        '납부상태': fee.status,
        '설명': fee.description,
        '등록일': formatDate(fee.created_at || fee.createdAt)
      }));

      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '관리비내역');

      const fileName = `계약${id}_관리비내역_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);

      success('엑셀 파일이 다운로드되었습니다.');
    } catch (error) {
      console.error('엑셀 다운로드 실패:', error);
      showError('엑셀 다운로드에 실패했습니다.');
    }
  };

  if (isLoadingContract || isLoadingFees) {
    return <div className="loading">관리비 정보를 불러오는 중...</div>;
  }

  if (!contract) {
    return (
      <div className="error">
        계약 정보를 찾을 수 없습니다.
      </div>
    );
  }

  const feeNumericAmount = (fee) => parseFloat(fee.amount) || 0;
  const totalAmount =
    managementFees?.reduce((sum, fee) => sum + feeNumericAmount(fee), 0) || 0;
  const paidAmount =
    managementFees?.reduce((sum, fee) => {
      if (fee.status === '납부완료' || fee.status === '부분납부') {
        return sum + feeNumericAmount(fee);
      }
      return sum;
    }, 0) || 0;
  const unpaidAmount = totalAmount - paidAmount;

  return (
    <div className="space-y-6">
      <div className="flex-between">
        <div className="flex items-center space-x-4">
          <Button
            onClick={() => navigate(`/contracts/${id}`)}
            variant="outline"
            icon={ArrowLeft}
          >
            계약 상세로
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">관리비 상세</h1>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {hasPermission(user?.role, 'MANAGEMENT_FEE_CREATE') && (
            <Button
              onClick={() => setShowAddForm(true)}
              variant="primary"
              icon={Plus}
            >
              관리비 추가
            </Button>
          )}
          {managementFees && managementFees.length > 0 && (
            <Button
              onClick={handleExcelDownload}
              variant="outline"
              icon={Download}
            >
              엑셀 다운로드
            </Button>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold">계약 정보</h2>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex items-center space-x-2">
              <User size={16} className="text-gray-400" />
              <span className="text-sm text-gray-600">사망자:</span>
              <span className="font-medium">{contract.deads?.[0]?.name || '-'}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Building size={16} className="text-gray-400" />
              <span className="text-sm text-gray-600">묘역:</span>
              <span className="font-medium">
                {contract.mausoleum ?
                  `${contract.mausoleum.complex}-${contract.mausoleum.area}-${contract.mausoleum.row_number}-${contract.mausoleum.number}` :
                  '-'
                }
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Calendar size={16} className="text-gray-400" />
              <span className="text-sm text-gray-600">계약일:</span>
              <span className="font-medium">{formatDate(contract.contract_date)}</span>
            </div>
            <div className="flex items-center space-x-2">
              <DollarSign size={16} className="text-gray-400" />
              <span className="text-sm text-gray-600">계약 평수:</span>
              <span className="font-medium">{contract.mausoleum?.area_size ? `${contract.mausoleum.area_size}평` : '-'}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card bg-blue-50">
          <div className="card-body text-center">
            <div className="text-2xl font-bold text-blue-600">
              {formatCurrency(totalAmount)}
            </div>
            <div className="text-sm text-gray-600">총 관리비</div>
          </div>
        </div>
        <div className="card bg-green-50">
          <div className="card-body text-center">
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(paidAmount)}
            </div>
            <div className="text-sm text-gray-600">납부 완료</div>
          </div>
        </div>
        <div className="card bg-red-50">
          <div className="card-body text-center">
            <div className="text-2xl font-bold text-red-600">
              {formatCurrency(unpaidAmount)}
            </div>
            <div className="text-sm text-gray-600">미납 금액</div>
          </div>
        </div>
      </div>

      {showAddForm && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold">관리비 추가</h3>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="form-label">관리기간 시작</label>
                <input
                  type="date"
                  value={newFee.period_start}
                  onChange={(e) => setNewFee(prev => ({ ...prev, period_start: e.target.value }))}
                  className="input"
                />
              </div>
              <div>
                <label className="form-label">관리기간 종료</label>
                <input
                  type="date"
                  value={newFee.period_end}
                  onChange={(e) => setNewFee(prev => ({ ...prev, period_end: e.target.value }))}
                  className="input"
                />
              </div>
              <div>
                <label className="form-label">금액</label>
                <NumberInput
                  value={newFee.amount}
                  onChange={(val) => setNewFee(prev => ({ ...prev, amount: val }))}
                  className="input"
                  placeholder="금액을 입력하세요"
                />
              </div>
              <div>
                <label className="form-label">납부기한</label>
                <input
                  type="date"
                  value={newFee.due_date}
                  onChange={(e) => setNewFee(prev => ({ ...prev, due_date: e.target.value }))}
                  className="input"
                />
              </div>
              <div className="md:col-span-2 lg:col-span-4">
                <label className="form-label">납부상태</label>
                <select
                  value={newFee.status}
                  onChange={(e) => setNewFee(prev => ({ ...prev, status: e.target.value }))}
                  className="select"
                >
                  <option value="미납">미납</option>
                  <option value="납부완료">납부완료</option>
                  <option value="부분납부">부분납부</option>
                </select>
              </div>
              <div className="md:col-span-2 lg:col-span-4">
                <label className="form-label">설명</label>
                <textarea
                  value={newFee.description}
                  onChange={(e) => setNewFee(prev => ({ ...prev, description: e.target.value }))}
                  className="input"
                  rows="3"
                  placeholder="관리비에 대한 설명을 입력하세요"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-4">
              <button
                onClick={() => setShowAddForm(false)}
                className="btn btn-outline"
              >
                취소
              </button>
              <button
                onClick={handleAddFee}
                disabled={addManagementFeeMutation.isLoading}
                className="btn btn-primary"
              >
                {addManagementFeeMutation.isLoading ? '추가 중...' : '추가'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold">관리비 내역</h2>
        </div>
        <div className="card-body">
          {managementFees && managementFees.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>관리기간</th>
                    <th className="text-left">금액</th>
                    <th>납부기한</th>
                    <th>납부상태</th>
                    <th>설명</th>
                    <th>등록일</th>
                    <th>작업</th>
                  </tr>
                </thead>
                <tbody>
                  {managementFees.map((fee) => (
                    <tr key={fee.id}>
                      <td className="font-medium">
                        {formatDate(fee.period_start)} ~ {formatDate(fee.period_end)}
                      </td>
                      <td className="font-medium text-left">
                        {formatCurrency(fee.amount)}
                      </td>
                      <td>{formatDate(fee.due_date)}</td>
                      <td>
                        <span className={`text-sm px-2 py-1 rounded ${
                          fee.status === '납부완료' ? 'bg-green-100 text-green-800' :
                            fee.status === '부분납부' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                        }`}>
                          {fee.status}
                        </span>
                      </td>
                      <td className="max-w-xs truncate" title={fee.description}>
                        {fee.description || '-'}
                      </td>
                      <td>{formatDate(fee.created_at || fee.createdAt)}</td>
                      <td>
                        <div className="flex items-center space-x-2 flex-wrap gap-1">
                          {hasPermission(user?.role, 'MANAGEMENT_FEE_EDIT') && (
                            <button
                              type="button"
                              onClick={() => handleOpenEditModal(fee)}
                              className="btn btn-outline btn-sm"
                              title="수정"
                            >
                              수정
                            </button>
                          )}
                          {hasPermission(user?.role, 'MANAGEMENT_FEE_DELETE') && (
                            <button
                              onClick={() => handleDeleteFee(fee.id)}
                              className="text-red-600 hover:text-red-800"
                              title="삭제"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <DollarSign size={48} className="mx-auto mb-4 text-gray-300" />
              <p>등록된 관리비 내역이 없습니다.</p>
              <p className="text-sm mt-2">관리비 추가 버튼으로 등록할 수 있습니다.</p>
            </div>
          )}
        </div>
      </div>

      {showEditModal && editingFeeId != null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">관리비 수정</h3>
              <button
                type="button"
                onClick={handleCloseEditModal}
                className="text-gray-400 hover:text-gray-600"
                aria-label="닫기"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">관리기간 시작 *</label>
                  <input
                    type="date"
                    value={editForm.period_start}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, period_start: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">관리기간 종료 *</label>
                  <input
                    type="date"
                    value={editForm.period_end}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, period_end: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">금액 *</label>
                <NumberInput
                  value={editForm.amount}
                  onChange={(val) => setEditForm((prev) => ({ ...prev, amount: val }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="금액"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">납부기한 *</label>
                <input
                  type="date"
                  value={editForm.due_date}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, due_date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">납부 상태 *</label>
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="미납">미납</option>
                  <option value="부분납부">부분납부</option>
                  <option value="납부완료">납부완료</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">설명</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  placeholder="비고·설명"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={handleCloseEditModal}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleSubmitEdit}
                disabled={updateManagementFeeMutation.isLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {updateManagementFeeMutation.isLoading ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractManagementFees;
